/**
 * Auth token storage.
 * Keys kept identical to the legacy `public/js/auth-helper.js` so the React app
 * and legacy HTML pages can share the same session during rollout.
 */

export const TOKEN_KEY = 'authToken';
export const REFRESH_TOKEN_KEY = 'refreshToken';
export const SESSION_ID_KEY = 'sessionId';
export const TOKEN_EXPIRY_KEY = 'tokenExpiry';
export const PERMISSIONS_KEY = 'permissions';
// Raw (untransformed) permissions object — needed by features that gate UI
// on RESTRICTIVE flags (e.g. `onlyMiddleTimeLeave`). Those flags must be
// read literally rather than through the admin-role bypass in
// `usePermission()`, otherwise an admin would unintentionally be subject
// to a restriction whenever the flag happens to be `true` in their record.
export const PERMISSIONS_RAW_KEY = 'permissionsRaw';
// Nav-section IDs to HIDE from the sidebar for this sub-user. This is an
// INDEPENDENT layer from the permission system — a section can be permitted
// yet still hidden here (and vice-versa). Empty array = nothing hidden.
export const MENU_HIDDEN_SECTIONS_KEY = 'menuHiddenSections';

export type RawPermissions = Record<string, unknown>;

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  sessionId?: string;
  expiresIn?: number;
  // Backend sends permissions as an object of boolean flags
  // ({ showDashboard: true, ... }); legacy/other callers may send a plain
  // string array. normalizePermissions() below coerces either to string[].
  permissions?: string[] | Record<string, unknown>;
  // Top-level list of nav-section IDs the sub-user should NOT see in the
  // sidebar (see `navConfig.NAV_ITEMS[].id`). Independent of `permissions`.
  menu_hidden_sections?: string[];
}

/**
 * Coerces any permission payload to the `string[]` shape the UI expects.
 *
 * `strictTrueOnly` keeps only entries whose value is literally `true`
 * (used by the superadmin "Login As" redirect so numeric fields like
 * `editTimeLimitMinutes` aren't surfaced as permissions).
 */
export function normalizePermissions(
  input: unknown,
  opts: { strictTrueOnly?: boolean } = {},
): string[] {
  if (!input) return [];
  if (Array.isArray(input)) return input.filter((x): x is string => typeof x === 'string');
  if (typeof input === 'object') {
    const match = opts.strictTrueOnly ? (v: unknown) => v === true : (v: unknown) => Boolean(v);
    return Object.entries(input as Record<string, unknown>)
      .filter(([, v]) => match(v))
      .map(([k]) => k);
  }
  return [];
}

/** Coerces any nav-hidden-sections payload to a clean `string[]`. */
export function normalizeHiddenSections(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input.filter((x): x is string => typeof x === 'string');
}

export const tokenStorage = {
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  getSessionId(): string | null {
    return localStorage.getItem(SESSION_ID_KEY);
  },
  getExpiry(): number | null {
    const v = localStorage.getItem(TOKEN_EXPIRY_KEY);
    return v ? parseInt(v, 10) : null;
  },
  getPermissions(): string[] {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    if (!raw) return [];
    try {
      return normalizePermissions(JSON.parse(raw));
    } catch {
      return [];
    }
  },
  getRawPermissions(): RawPermissions | null {
    const raw = localStorage.getItem(PERMISSIONS_RAW_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as RawPermissions)
        : null;
    } catch {
      return null;
    }
  },
  getHiddenMenuSections(): string[] {
    const raw = localStorage.getItem(MENU_HIDDEN_SECTIONS_KEY);
    if (!raw) return [];
    try {
      return normalizeHiddenSections(JSON.parse(raw));
    } catch {
      return [];
    }
  },
  isExpiring(bufferMs = 5 * 60 * 1000): boolean {
    const expiry = this.getExpiry();
    if (!expiry) return true;
    return expiry - Date.now() < bufferMs;
  },
  store(data: LoginResponse): void {
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    if (data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    if (data.sessionId) localStorage.setItem(SESSION_ID_KEY, data.sessionId);
    if (data.expiresIn) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + data.expiresIn * 1000));
    }
    if (data.permissions) {
      localStorage.setItem(
        PERMISSIONS_KEY,
        JSON.stringify(normalizePermissions(data.permissions)),
      );
      // Persist the untransformed object too. Only object payloads are
      // meaningful here — a plain string[] response carries no booleans
      // to read literally, so we skip storing it.
      if (typeof data.permissions === 'object' && !Array.isArray(data.permissions)) {
        localStorage.setItem(
          PERMISSIONS_RAW_KEY,
          JSON.stringify(data.permissions),
        );
      } else {
        localStorage.removeItem(PERMISSIONS_RAW_KEY);
      }
    }
    // Persist the hidden-sections layer alongside permissions so it survives
    // reload (mirrors how `permissions` is rehydrated on cold boot). Only
    // write when the field is present so an older backend response that omits
    // it doesn't clobber a previously stored value.
    if (data.menu_hidden_sections !== undefined) {
      localStorage.setItem(
        MENU_HIDDEN_SECTIONS_KEY,
        JSON.stringify(normalizeHiddenSections(data.menu_hidden_sections)),
      );
    }
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem(PERMISSIONS_RAW_KEY);
    localStorage.removeItem(MENU_HIDDEN_SECTIONS_KEY);
  },
};
